import { Injectable } from '@angular/core';

/** Gemerkter Zustand des Tabs „Einsätze & Reparaturen" eines Kunden. */
export interface EinsatzAnsichtState {
  /** Schlüssel des Personenfilters, oder `all`. */
  personFilter: string;

  /**
   * Rohe Scrollposition des Scrollbereichs in Pixeln. Rückfallebene, falls der
   * Anker nicht mehr gefunden wird.
   */
  scrollY: number;

  /**
   * Id des obersten sichtbaren Einsatzes. Der Anker ist genauer als eine reine
   * Pixelposition: er trifft auch dann, wenn die Liste beim Zurückkommen anders
   * hoch ist (nachgeladene Daten, geänderte Zeilenumbrüche).
   */
  ankerId: number | null;

  /** Abstand des Ankers zur Oberkante des Scrollbereichs, in Pixeln. */
  ankerOffset: number;
}

/**
 * Merkt sich je Kunde den Zustand der Einsatzliste – Personenfilter und
 * Scrollposition –, damit ein Sprung in einen Einsatz und zurück dort weitergeht,
 * wo man aufgehört hat.
 *
 * Der Zustand lebt bewusst nur bis zum nächsten Besuch der Kundenliste bzw. des
 * Wartungs-Dashboards: wer eine Ebene weiter zurückgeht, fängt beim erneuten
 * Öffnen eines Kunden von vorne an (siehe <c>alleVerwerfen</c>). Zwischen Kunden
 * getrennt gespeichert, damit ein Filter nicht auf einen anderen Kunden
 * überspringt.
 *
 * Ablage im sessionStorage – wie schon beim aktiven Tab der Kundendetailseite.
 * Das übersteht ein versehentliches Neuladen, endet aber mit dem Tab.
 */
@Injectable({ providedIn: 'root' })
export class EinsatzAnsichtStateService {
  private readonly keyPrefix = 'wartung-einsatz-ansicht-';

  /** Zustand eines Kunden merken. Ein fehlender Speicher ist kein Fehler. */
  merken(customerId: number, state: EinsatzAnsichtState): void {
    if (!customerId) return;

    try {
      sessionStorage.setItem(
        this.keyPrefix + customerId,
        JSON.stringify({
          personFilter: state.personFilter || 'all',
          scrollY: Math.max(0, Math.round(state.scrollY || 0)),
          ankerId: state.ankerId ?? null,
          ankerOffset: Math.round(state.ankerOffset || 0)
        })
      );
    } catch {
      // Privater Modus oder voller Speicher: dann eben ohne Merken.
    }
  }

  /** Gemerkter Zustand, oder `null`. */
  holen(customerId: number): EinsatzAnsichtState | null {
    if (!customerId) return null;

    try {
      const roh = sessionStorage.getItem(this.keyPrefix + customerId);
      if (!roh) return null;

      const gelesen = JSON.parse(roh);
      if (!gelesen || typeof gelesen !== 'object') return null;

      return {
        personFilter: typeof gelesen.personFilter === 'string' ? gelesen.personFilter : 'all',
        scrollY: typeof gelesen.scrollY === 'number' && gelesen.scrollY > 0 ? gelesen.scrollY : 0,
        ankerId: typeof gelesen.ankerId === 'number' ? gelesen.ankerId : null,
        ankerOffset: typeof gelesen.ankerOffset === 'number' ? gelesen.ankerOffset : 0
      };
    } catch {
      return null;
    }
  }

  verwerfen(customerId: number): void {
    try {
      sessionStorage.removeItem(this.keyPrefix + customerId);
    } catch {
      // ignorieren
    }
  }

  /**
   * Alles vergessen. Wird beim Öffnen der Kundenliste und des Wartungs-
   * Dashboards aufgerufen: von dort aus soll jeder Kunde wieder ungefiltert und
   * von oben starten.
   */
  alleVerwerfen(): void {
    try {
      const zuLoeschen = Object.keys(sessionStorage)
        .filter(key => key.startsWith(this.keyPrefix));

      for (const key of zuLoeschen) {
        sessionStorage.removeItem(key);
      }
    } catch {
      // ignorieren
    }
  }
}
